import * as _ from "lodash"
import { IObjectTreeFolder, ITreeObject } from "../PA.Core.IF.ObjectTree"
import { ObjectTree } from "./ObjectTree"

export class ObjectTreeUtils {
    static canMoveAll(
        arrSrc: (ITreeObject | IObjectTreeFolder)[],
        tgt: ITreeObject | IObjectTreeFolder,
        srcLocation?: IObjectTreeFolder,
    ): boolean {
        if (_.isEmpty(arrSrc)) return false
        for (const obj of arrSrc) {
            if (!ObjectTreeUtils.canMove(obj, tgt, srcLocation)) return false
        }
        return true
    }

    static canMove(
        src: ITreeObject | IObjectTreeFolder,
        tgt: ITreeObject | IObjectTreeFolder,
        srcLocation?: IObjectTreeFolder,
    ): boolean {
        let operation = `${ObjectTreeUtils.treeObjectType(src)}-${ObjectTreeUtils.treeObjectType(tgt)}`
        switch (operation) {
            case "TreeObject-TreeFolder":
                {
                    let srcO = <ITreeObject>src
                    let tgtF = <IObjectTreeFolder>tgt
                    // No more validations, it may be at the same location, but we are not validating that
                    return true
                }
                break

            case "TreeFolder-TreeFolder":
                {
                    let srcF = <IObjectTreeFolder>src
                    let tgtF = <IObjectTreeFolder>tgt
                    // Can't be the same source and target.
                    if (srcF.wbId == tgtF.wbId) return false

                    // Have to be in the same Tree
                    if (srcF.Tree.id != tgtF.Tree.id) return false

                    // Source can't be a parent of source
                    let srcIsParentOfTgt = false
                    let curFolder = tgtF
                    while (true) {
                        if (curFolder.wbId == srcF.wbId) {
                            srcIsParentOfTgt = true
                            break
                        }

                        if (_.isEmpty(curFolder.Parent)) break

                        curFolder = curFolder.Parent
                    }

                    if (srcIsParentOfTgt) return false

                    return true
                }
                break

            case "TreeFolder-TreeObject":
                return false
            default:
                return false
        }
    }

    static async tryMoveAll(
        arrSrc: (ITreeObject | IObjectTreeFolder)[],
        tgt: ITreeObject | IObjectTreeFolder,
    ): Promise<boolean> {
        if (!ObjectTreeUtils.canMoveAll(arrSrc, tgt)) return false

        // Do this in bulk if source are TreeObjects and targets is Folder
        if (ObjectTreeUtils.isTreeObjectsOnly(arrSrc) && ObjectTreeUtils.treeObjectType(tgt) == "TreeFolder") {
            const srcObjs = ObjectTreeUtils.treeObjectsOnly(arrSrc)
            const srcO = srcObjs[0]
            const tgtF = <IObjectTreeFolder>tgt
            // No more validations, it may be at the same location, but we are not validating that
            const tree = tgtF.Tree
            const dm = tree.Root.ContentDataManager
            await dm.moveObjectsToFolder(
                srcO.mapperName,
                _.map(srcObjs, (a) => a.wb_id),
                tgtF.wbId,
            )
            return true
        }

        // Fall back to individual moves for folder to folder
        for (const srcObj of arrSrc) {
            await ObjectTreeUtils.tryMove(srcObj, tgt)
        }
    }

    static async tryMove(src: ITreeObject | IObjectTreeFolder, tgt: ITreeObject | IObjectTreeFolder): Promise<boolean> {
        if (!ObjectTreeUtils.canMove(src, tgt)) return false

        let operation = `${ObjectTreeUtils.treeObjectType(src)}-${ObjectTreeUtils.treeObjectType(tgt)}`
        switch (operation) {
            case "TreeObject-TreeFolder":
                {
                    let srcO = <ITreeObject>src
                    let tgtF = <IObjectTreeFolder>tgt
                    // No more validations, it may be at the same location, but we are not validating that
                    let tree = tgtF.Tree
                    let dm = tree.Root.ContentDataManager
                    await dm.moveObjectsToFolder(srcO.mapperName, [srcO.wb_id], tgtF.wbId)
                    return true
                }
                break

            case "TreeFolder-TreeFolder":
                {
                    let srcF = <IObjectTreeFolder>src
                    let tgtF = <IObjectTreeFolder>tgt
                    let tree = srcF.Tree
                    await tree.moveFolderTo(srcF.wbId, tgtF.wbId)
                    return true
                }
                break

            case "TreeFolder-TreeObject":
                return false

            default:
                return false
        }
    }

    static canCutAll(arrObjs: (ITreeObject | IObjectTreeFolder)[]): boolean {
        if (_.isEmpty(arrObjs)) return false
        if (ObjectTreeUtils.isTreeObjectsOnly(arrObjs)) {
            return true
        }

        if (ObjectTreeUtils.isTreeFoldersOnly(arrObjs)) {
            if (arrObjs.length == 1) return true
        }

        return false
    }

    static treeObjectType(obj: ITreeObject | IObjectTreeFolder): "TreeObject" | "TreeFolder" | "Unknown" {
        if (_.isEmpty(obj)) return undefined

        // @ts-expect-error "obj can be any"
        if (!_.isEmpty(obj?.mapperName)) return "TreeObject"

        // @ts-expect-error "obj can be any"
        if (_.isArray(obj.SubFolders)) return "TreeFolder"

        return "Unknown"
    }

    static isTreeObject(obj: unknown): obj is ITreeObject {
        return obj && !_.isEmpty((obj as ITreeObject).mapperName)
    }

    static getDescription(obj: ITreeObject | IObjectTreeFolder, bPrefixType?: boolean): string {
        const toType = ObjectTreeUtils.treeObjectType(obj)
        switch (toType) {
            case "TreeObject": {
                const to: ITreeObject = <ITreeObject>obj
                const desc = `${to.name} (${to.id_ui})`
                if (bPrefixType) return `Object: ${desc}`
                else return desc
            }
            case "TreeFolder": {
                const tf: IObjectTreeFolder = <IObjectTreeFolder>obj
                const desc = `${tf.Name}`
                if (bPrefixType) return `Folder: ${desc}`
                else return desc
            }
            default:
                return undefined
        }
    }

    static getDescriptions(
        arrObjs: (ITreeObject | IObjectTreeFolder)[],
        bPrefixType?: boolean,
        bIncludeUnknown?: boolean,
    ): string[] {
        const rv: string[] = []
        _.forEach(arrObjs, (obj) => {
            const desc = ObjectTreeUtils.getDescription(obj, bPrefixType)
            if (!bIncludeUnknown && _.isUndefined(desc)) return
            rv.push(desc || "Unknown")
        })

        return rv
    }

    static treeObjectsOnly(arrObjs: (ITreeObject | IObjectTreeFolder)[], mapperId?: string): ITreeObject[] {
        let rv: ITreeObject[] = []

        rv = <ITreeObject[]>_.filter(arrObjs, (a) => ObjectTreeUtils.treeObjectType(a) == "TreeObject")

        if (!_.isEmpty(mapperId)) rv = _.filter(rv, (a) => a.mapperName == mapperId)

        return rv
    }

    static isTreeObjectsOnly(arrObjs: (ITreeObject | IObjectTreeFolder)[]): boolean {
        for (const obj of arrObjs) {
            if (ObjectTreeUtils.treeObjectType(obj) != "TreeObject") return false
        }

        return true
    }

    static isTreeFoldersOnly(arrObjs: (ITreeObject | IObjectTreeFolder)[]): boolean {
        for (const obj of arrObjs) {
            if (ObjectTreeUtils.treeObjectType(obj) != "TreeFolder") return false
        }

        return true
    }

    static canDelete(curFolder: IObjectTreeFolder, deleteObj: ITreeObject): boolean {
        if (_.isEmpty(curFolder) || _.isEmpty(deleteObj)) return false

        let dm = curFolder.ContentDataManager || curFolder.Tree.Root.ContentDataManager
        let mapperId = deleteObj.mapperName
        return !_.isUndefined(dm?.options?.mapper?.[mapperId]?.BO)
    }

    static canDeleteAll(curFolder: IObjectTreeFolder, arrObjs: (ITreeObject | IObjectTreeFolder)[]): boolean {
        if (_.isEmpty(arrObjs)) return false
        if (ObjectTreeUtils.isTreeObjectsOnly(arrObjs)) {
            for (let obj of arrObjs) {
                let to: ITreeObject = <ITreeObject>obj
                if (!ObjectTreeUtils.canDelete(curFolder, to)) return false
            }
            return true
        }

        if (ObjectTreeUtils.isTreeFoldersOnly(arrObjs)) {
            if (arrObjs.length == 1) return true
        }

        return false
    }
}
